














































import {Component, Prop, Vue} from 'vue-property-decorator';
import {SelectableStore} from '@/components/mass-import/SelectableStore';
import {Inject} from 'vue-typedi';
import {ProductService} from '@/services/ProductService';
import AlertMessage from '@/components/mass-import/steps/AlertMessage.vue';
import {AxiosError, AxiosResponse} from 'axios';
import {massImportCsvConstraints} from '@/components/mass-import/MassImportCsvConstraints';
import {CsvLine, CsvValidation} from '@/helpers/csv/CsvValidation';
import StoreSelectionSummary from '@/components/mass-import/steps/StoreSelectionSummary.vue';
import {
  LINE_LIMIT_SITE_SELECTION_LEVEL_1,
  LINE_LIMIT_SITE_SELECTION_LEVEL_2,
  SITE_SELECT_LIMIT_LEVEL_1
} from '@/components/mass-import/MassImportConfig';
import CsvFileTable from '@/components/mass-import/steps/CsvFileTable.vue';

@Component({
  components: {AlertMessage, CsvFileTable, StoreSelectionSummary}
})
export default class BackupProductsStep extends Vue {
  private static readonly MASS_IMPORT_CSV_CONSTRAINT_CUG_INDEX: number = 0;
  private static readonly DEFAULT_ERROR_MESSAGE: string = 'Une erreur s\'est produite. Veuillez réessayer plus tard.';
  private static readonly TIMEOUT_ERROR_MESSAGE: string = 'La création de la sauvegarde est trop importante. Veuillez réessayer en diminuant le nombre de sites sélectionnés';
  private static readonly LOCK_ERROR_MESSAGE: string = 'Une mise à jour est actuellement en cours. La mise à jour en masse est brièvement verrouillée.';
  private static readonly TOO_MANY_FIELDS_ERROR_MESSAGE: string = 'Trop de valeurs';
  private static readonly INCOMPLETE_LINE_ERROR_MESSAGE: string = 'Ligne incomplète';

  @Prop({default: []}) readonly selectableStores: SelectableStore[];
  @Prop({default: {value: false}}) readonly allStoresSelectedContainer: {value: boolean};
  @Prop({default: {value: null}}) readonly downloadedBackupBlobHrefContainer: {value?: string};
  @Prop({default: {value: null}}) readonly uploadedFileContainer: {value?: File};
  @Prop({default: {value: new CsvValidation()}}) readonly uploadedFileCsvValidationContainer: {value: CsvValidation};
  @Prop({default: () => {}}) readonly previousStepHandler: () => void;
  @Prop({default: () => {}}) readonly nextStepHandler: () => void;

  @Inject()
  productService!: ProductService;

  public readonly errorMessages: String[] = [];
  public backupDownloadInProgress: boolean = false;
  public backupDownloadError: boolean = false;
  public backupDownloadErrorMessage: string = BackupProductsStep.DEFAULT_ERROR_MESSAGE;
  public backupFileClicked: boolean = false;
  public backupFileReady: boolean = false;

  mounted() {
    this.initStep();
  }

  private initStep() {
    if (this.downloadedBackupBlobHrefContainer.value) {
      this.backupFileClicked = true;
      this.backupFileReady = true;
    }
    this.validateUploadedFile();
  }

  get lineCountLimit() {
    return this.selectableStores.filter(selectableStore => this.allStoresSelectedContainer.value || selectableStore.checked).length > SITE_SELECT_LIMIT_LEVEL_1
        ? LINE_LIMIT_SITE_SELECTION_LEVEL_2
        : LINE_LIMIT_SITE_SELECTION_LEVEL_1;
  }

  get maxLineCountReached() {
    return this.uploadedFileCsvValidationContainer.value.lines.length > this.lineCountLimit;
  }

  get downloadBackupDisabled() {
    return this.backupDownloadInProgress
        || this.errorMessages.length !== 0
        || this.uploadedFileCsvValidationContainer.value.lines.length === 0
        || this.maxLineCountReached;
  }

  public downloadBackup() {
    this.backupDownloadInProgress = true;
    this.backupDownloadError = false;
    const backupStoreIds = this.selectableStores
        .filter(selectableStore => this.allStoresSelectedContainer.value || selectableStore.checked)
        .map(selectableStore => selectableStore.store.storeId);
    const backupCugs: string[] = this.uploadedFileCsvValidationContainer.value.lines
        .map((csvLine: CsvLine) => csvLine.fields[BackupProductsStep.MASS_IMPORT_CSV_CONSTRAINT_CUG_INDEX].value);
    this.productService.getMassImportBackup(backupStoreIds, backupCugs)
        .then((response: AxiosResponse) => response.data)
        .then(blob => window.URL.createObjectURL(blob))
        .then((url: string) => {
          this.downloadedBackupBlobHrefContainer.value = url;
          this.backupFileReady = true;
          this.backupDownloadError = false;
        })
        .catch((error: AxiosError) => {
          this.backupFileReady = false;
          this.backupDownloadError = true;
          if (error.response && error.response.status === 423) {
            this.backupDownloadErrorMessage = BackupProductsStep.LOCK_ERROR_MESSAGE;
          } else if (error.code && error.code === 'ECONNABORTED') {
            // timeout reached
            this.backupDownloadErrorMessage = BackupProductsStep.TIMEOUT_ERROR_MESSAGE;
          } else {
            this.backupDownloadErrorMessage = BackupProductsStep.DEFAULT_ERROR_MESSAGE;
          }
        })
        .finally(() => {
          this.backupDownloadInProgress = false;
        });
  }

  public saveBackup() {
    this.backupFileClicked = true;
    this.nextStepHandler();
  }

  private async validateUploadedFile() {
    if (!this.uploadedFileContainer.value) {
      return;
    }

    await this.uploadedFileContainer.value.text()
        .then((content: string) => {
          this.uploadedFileCsvValidationContainer.value.apply(content.trim());
          this.uploadedFileCsvValidationContainer.value.validate(massImportCsvConstraints);
          if (!this.uploadedFileCsvValidationContainer.value.valid) {
            this.uploadedFileCsvValidationContainer.value.getInvalidLines().forEach(invalidLine => {
              this.errorMessages.push(`Ligne ${invalidLine.position + 1} : ${this.getInvalidLineErrorMessage(invalidLine)}`);
            })
          }
        });
  }

  private getInvalidLineErrorMessage(csvLine: CsvLine): string {
    const errorMessages = [...csvLine.fields.filter(csvField => !csvField.valid)
        .map(errorField => massImportCsvConstraints.get(errorField.position)?.description)
        .filter(value => value != null)
    ];
    if (csvLine.fields.length < massImportCsvConstraints.size) {
      errorMessages.push(BackupProductsStep.INCOMPLETE_LINE_ERROR_MESSAGE);
    } else if (csvLine.fields.length > massImportCsvConstraints.size) {
      errorMessages.push(BackupProductsStep.TOO_MANY_FIELDS_ERROR_MESSAGE);
    }

    return errorMessages.join(', ');
  }
}
